import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { mastersService } from '../utils/apiService';
import ConfirmationModal from '../components/print/ConfirmationModal';
import { useTranslation } from '../hooks/useTranslation';

// Without TabPanel, simplify the component
const OtherMasters = () => {
  const navigate = useNavigate();
  const { currentTenant } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const { t } = useTranslation();
  const [masterData, setMasterData] = useState({
    itemGroups: [],
    units: [],
    paymentMethods: [],
    loyalty: [],
  });
  
  const paymentCategories = [
    { id: 'cash', key: 'Cash', name: 'Cash' },
    { id: 'card', key: 'Card', name: 'Card' },
    { id: 'mobile_wallet', key: 'Mobile_Wallet', name: 'Mobile Wallet' },
    { id: 'bank_transfer', key: 'Bank_Transfer', name: 'Bank Transfer' },
    { id: 'other', key: 'Other', name: 'Other' }
  ];

  const getCategoryNameById = (categoryId) => {
    const category = paymentCategories.find(cat => 
      cat.id === categoryId || 
      cat.key.toLowerCase() === (categoryId || '').toLowerCase() ||
      cat.name.toLowerCase() === (categoryId || '').toLowerCase()
    );
    return category ? t(`OtherMasters.${category.key}`) : categoryId;
  };

  const filteredData = (type) => {
    if (!masterData) {
      console.log('masterData is undefined or null');
      return [];
    }
    
    const typeKey = typeof type === 'string' ? type : type.dataKey;
    const items = masterData[typeKey] || [];
    
    console.log(`Filtered data for ${typeKey}:`, items);
    return items;
  };

  // Master types configuration
 const masterTypes = [
  {
    id: 'itemGroups',
    label: 'Item_Groups',
    dataKey: 'itemGroups',
    fields: [
      { name: 'name', label: 'Group_Name', required: true },
      { name: 'description', label: 'Description', required: false },
    ],
  },
  {
    id: 'units',
    label: 'Units',
    dataKey: 'units',
    fields: [
      { name: 'name', label: 'Unit_Name', required: true },
      { name: 'symbol', label: 'Code', required: true },
      { name: 'description', label: 'Description', required: false },
    ],
  },
  {
    id: 'paymentMethods',
    label: 'Payment_Methods',
    dataKey: 'paymentMethods',
    fields: [
      {
        name: 'category',
        label: 'Category',
        required: true,
        type: 'select',
        options: paymentCategories,
      },
      { name: 'name', label: 'Method_Name', required: true },
      { name: 'subcategory', label: 'Subcategory', required: false },
      { name: 'description', label: 'Description', required: false },
    ],
  },
  {
    id: 'loyalty',
    label: 'Loyalty',
    dataKey: 'loyalty',
    fields: [
      { name: 'name', label: 'Loyalty_Name', required: true },
      { name: 'email', label: 'Email', required: false },
      { name: 'phone', label: 'Phone', required: false },
      { name: 'description', label: 'Description', required: false },
    ],
  },
];

  useEffect(() => {
    fetchMasterData();
  }, [currentTenant]);

  const fetchMasterData = async () => {
    try {
      console.log('Fetching master data for tenant:', currentTenant.id);
      setLoading(true);
      setError(null);
      
      const data = await mastersService.getAllMasters(currentTenant.id);
      console.log('Fetched data:', data);
      setMasterData(data);
    } catch (error) {
      console.error('Error in fetchMasterData:', error);
      setError(error.message);
      showSnackbar(t('OtherMasters.Failed_To_Fetch_Master_Data'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      console.log('Editing item:', item);
      setEditingItem(item);
      setFormData(item);
    } else {
      setEditingItem(null);
      if (activeTab === 2) { // Payment Methods
        // Initialize with empty values but ensure all fields exist
        setFormData({ 
          name: '', 
          category: '', 
          subcategory: '', 
          description: '' 
        });
      } else if (activeTab === 1) { // Units
        setFormData({ name: '', symbol: '', description: '' });
      } else { // Item Groups
        setFormData({ name: '', description: '' });
      }
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingItem(null);
    setFormData({ name: '', description: '' });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const currentMasterType = masterTypes[activeTab];

    try {
      // Validate required fields
      const missingFields = currentMasterType.fields
        .filter(field => field.required && !formData[field.name])
        .map(field => t(`OtherMasters.${field.label}`));

      if (missingFields.length > 0) {
        throw new Error(t('OtherMasters.Please_Fill_Required_Fields', { fields: missingFields.join(', ') }));
      }

      // Add detailed logging for payment methods
      if (currentMasterType.id === 'paymentMethods') {
        console.log('Payment Method submission details:', {
          name: formData.name,
          category: formData.category,
          subcategory: formData.subcategory,
          description: formData.description,
          fullFormData: JSON.stringify(formData)
        });
      }
      
      console.log('Submitting master data:', {
        masterType: currentMasterType.id,
        tenantId: currentTenant.id,
        formData
      });

      if (editingItem) {
        await mastersService.updateMaster(currentMasterType.id, currentTenant.id, editingItem._id, formData);
      } else {
        await mastersService.createMaster(currentMasterType.id, currentTenant.id, formData);
      }

      console.log('Submit success');
      await fetchMasterData();
      handleCloseDialog();
      showSnackbar(`${t(`OtherMasters.${currentMasterType.label}`)} ${editingItem ? t('OtherMasters.Updated') : t('OtherMasters.Added')} ${t('OtherMasters.Successfully')}`);
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      showSnackbar(error.message || (editingItem ? t('OtherMasters.Failed_To_Update', { item: t(`OtherMasters.${currentMasterType.label}`) }) : t('OtherMasters.Failed_To_Add', { item: t(`OtherMasters.${currentMasterType.label}`) })), 'error');
    }
  };

  const handleDeleteClick = (item) => {
    setSelectedItem(item);
    setDeleteModalOpen(true);
  };

  const handleDelete = async (item) => {
    const currentMasterType = masterTypes[activeTab];
    
    try {
      await mastersService.deleteMaster(currentMasterType.id, currentTenant.id, item._id);
      await fetchMasterData();
      showSnackbar(`${t(`OtherMasters.${currentMasterType.label}`)} ${t('OtherMasters.Deleted')} ${t('OtherMasters.Successfully')}`);
    } catch (error) {
      console.error('Error in handleDelete:', error);
      showSnackbar(error.message || t('OtherMasters.Failed_To_Delete', { item: t(`OtherMasters.${currentMasterType.label}`) }), 'error');
    }
  };

  const handleDeleteConfirm = async () => {
    await handleDelete(selectedItem);
    setDeleteModalOpen(false);
    setSelectedItem(null);
  };

  return (
    <Container maxWidth={false} sx={{ mt: 0.5, px: { xs: 0.5, sm: 1, md: 1, lg: 1 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        <Box>
          <Typography variant="h5" component="h1" fontWeight="bold">
           {t('OtherMasters.Master_Data')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
           {t('OtherMasters.Manage_Master_Data')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          size="small"
          onClick={() => handleOpenDialog()}
          sx={{ 
            borderRadius: '4px',
            textTransform: 'none',
            py: 0.5,
            px: 1,
          }}
        >
         {t('OtherMasters.Add')} {t(`OtherMasters.${masterTypes[activeTab]?.label}`)}
        </Button>
      </Box>

      <Tabs 
        value={activeTab} 
        onChange={handleTabChange}
        aria-label="master data tabs"
        variant="scrollable"
        scrollButtons="auto"
        sx={{ 
          borderBottom: 1, 
          borderColor: 'divider',
          mb: 0.5,
          minHeight: 32,
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 500,
            minWidth: 100,
            py: 0.25,
            minHeight: 32,
            fontSize: '0.875rem',
          }
        }}
      >
        {masterTypes.map((type) => (
          <Tab key={type.id} label={t(`OtherMasters.${type.label}`)} />
        ))}
      </Tabs>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper 
        elevation={0} 
        sx={{ 
          p: 0.5, 
          mb: 0.5, 
          border: '1px solid rgba(0, 0, 0, 0.12)', 
          borderRadius: '4px',
        }}
      >
        <TableContainer>
          <Table sx={{ minWidth: 650, '& td, & th': { borderColor: 'rgba(0, 0, 0, 0.12)' } }} size="small">
            <TableHead>
              <TableRow sx={{ '& th': { borderBottom: 1, borderColor: 'rgba(0, 0, 0, 0.12)' } }}>
                {masterTypes[activeTab].id === 'paymentMethods' && (
                  <TableCell sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.75, minWidth: '100px' }}>{t('OtherMasters.Category')}</TableCell>
                )}
                <TableCell sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.75, minWidth: '150px' }}>{t('OtherMasters.Name')}</TableCell>
                {masterTypes[activeTab].id === 'units' && <TableCell sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.75, minWidth: '80px' }}>{t('OtherMasters.Code')}</TableCell>}
                {masterTypes[activeTab].id === 'paymentMethods' && (
                  <>
                    <TableCell sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.75, minWidth: '120px' }}>{t('OtherMasters.Subcategory')}</TableCell>
                  </>
                )}
                {masterTypes[activeTab].id === 'loyalty' && (
                  <>
                    <TableCell sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.75, minWidth: '150px' }}>{t('OtherMasters.Email')}</TableCell>
                    <TableCell sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.75, minWidth: '120px' }}>{t('OtherMasters.Phone')}</TableCell>
                  </>
                )}
                <TableCell sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.75, minWidth: '200px' }}>{t('OtherMasters.Description')}</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.75, width: '100px' }}>{t('OtherMasters.Actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={masterTypes[activeTab].id === 'paymentMethods' ? 5 : (masterTypes[activeTab].id === 'units' ? 4 : (masterTypes[activeTab].id === 'loyalty' ? 5 : 3))} align="center">{t('Common.Loading')}</TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={masterTypes[activeTab].id === 'paymentMethods' ? 5 : (masterTypes[activeTab].id === 'units' ? 4 : (masterTypes[activeTab].id === 'loyalty' ? 5 : 3))} align="center" sx={{ py: 2 }}>
                    <Alert severity="error">{error}</Alert>
                  </TableCell>
                </TableRow>
              ) : filteredData(masterTypes[activeTab]).length === 0 ? (
                <TableRow>
                  <TableCell colSpan={masterTypes[activeTab].id === 'paymentMethods' ? 5 : (masterTypes[activeTab].id === 'units' ? 4 : (masterTypes[activeTab].id === 'loyalty' ? 5 : 3))} align="center" sx={{ py: 2 }}>
                    {t('OtherMasters.No_Items_Found')}
                  </TableCell>
                </TableRow>
              ) : (
                filteredData(masterTypes[activeTab])
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((item) => (
                    <TableRow 
                      key={item._id}
                      hover
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      {masterTypes[activeTab].id === 'paymentMethods' && (
                        <TableCell sx={{ py: 0.75 }}>{getCategoryNameById(item.category) || '-'}</TableCell>
                      )}
                      <TableCell sx={{ py: 0.75 }}>{item.name || '-'}</TableCell>
                      {masterTypes[activeTab].id === 'units' && <TableCell sx={{ py: 0.75 }}>{item.symbol || '-'}</TableCell>}
                      {masterTypes[activeTab].id === 'paymentMethods' && (
                        <>
                          <TableCell sx={{ py: 0.75 }}>{item.subcategory || '-'}</TableCell>
                        </>
                      )}
                      {masterTypes[activeTab].id === 'loyalty' && (
                        <>
                          <TableCell sx={{ py: 0.75 }}>{item.email || '-'}</TableCell>
                          <TableCell sx={{ py: 0.75 }}>{item.phone || '-'}</TableCell>
                        </>
                      )}
                      <TableCell sx={{ py: 0.75 }}>{item.description || '-'}</TableCell>
                      <TableCell align="center" sx={{ py: 0.75 }}>
                        {masterTypes[activeTab].id !== 'loyalty' && (
                          <IconButton
                            size="small"
                            color="primary"
                            title={t('OtherMasters.Edit')}
                            aria-label={t('OtherMasters.Edit')}
                            sx={{ p: 0.5, mr: 0.5 }}
                            onClick={() => handleOpenDialog(item)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          color="error"
                          title={t('OtherMasters.Delete')}
                          aria-label={t('OtherMasters.Delete')}
                          sx={{ p: 0.5 }}
                          onClick={() => handleDeleteClick(item)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filteredData(masterTypes[activeTab]).length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          sx={{
            borderTop: '1px solid rgba(0, 0, 0, 0.12)',
            '& .MuiTablePagination-select': {
              py: 0.25,
            },
            '& .MuiTablePagination-displayedRows': {
              fontSize: '0.875rem',
            }
          }}
        />
      </Paper>

      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="xs" 
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: '4px',
            boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.15)',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)', 
          px: 2, 
          py: 1,
          fontSize: '0.95rem',
          fontWeight: 'medium',
          backgroundColor: '#f7f7f7'
        }}>
          {editingItem ? t('OtherMasters.Edit') : t('OtherMasters.Add')} {t(`OtherMasters.${masterTypes[activeTab]?.label}`)}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {masterTypes[activeTab]?.fields.map((field) => (
                field.type === 'select' ? (
                  <FormControl 
                    key={field.name} 
                    fullWidth 
                    size="small"
                    required={field.required}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '4px',
                        fontSize: '0.875rem'
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '0.875rem'
                      }
                    }}
                  >
                    <InputLabel id={`${field.name}-label`}>{t(`OtherMasters.${field.label}`)}</InputLabel>
                    <Select
                      labelId={`${field.name}-label`}
                      id={field.name}
                      name={field.name}
                      value={formData[field.name] || ''}
                      onChange={handleInputChange}
                      label={t(`OtherMasters.${field.label}`)}
                      sx={{
                        '& .MuiSelect-select': {
                          padding: '8px 10px',
                        }
                      }}
                    >
                      {field.options.map(option => (
                        <MenuItem key={option.id} value={option.id}>{t(`OtherMasters.${option.key || option.name}`)}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <TextField
                    key={field.name}
                    name={field.name}
                    label={t(`OtherMasters.${field.label}`)}
                    value={formData[field.name] || ''}
                    onChange={handleInputChange}
                    required={field.required}
                    fullWidth
                    size="small"
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '4px',
                        fontSize: '0.875rem'
                      },
                      '& .MuiOutlinedInput-input': {
                        padding: '8px 10px',
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '0.875rem'
                      }
                    }}
                  />
                )
              ))}
            </Box>
          </DialogContent>
          <DialogActions sx={{ 
            px: 2, 
            py: 1,
            borderTop: '1px solid rgba(0, 0, 0, 0.08)',
            backgroundColor: '#f7f7f7',
            gap: 1
          }}>
            <Button 
              onClick={handleCloseDialog}
              variant="outlined"
              size="small"
              sx={{ 
                borderRadius: '4px',
                textTransform: 'none',
                px: 1.5,
                py: 0.5,
                minWidth: '60px',
                fontSize: '0.8rem'
              }}
            >
              {t('Common.Cancel')}
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
              size="small"
              sx={{ 
                borderRadius: '4px',
                textTransform: 'none',
                px: 1.5,
                py: 0.5,
                minWidth: '60px',
                fontSize: '0.8rem'
              }}
            >
           {editingItem ? t('OtherMasters.Update') : t('OtherMasters.Add')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <ConfirmationModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        title={`${t('OtherMasters.Delete')} ${t(`OtherMasters.${masterTypes[activeTab]?.label}`)}`}
        message={t('OtherMasters.Confirm_Delete')}
        confirmText={t('OtherMasters.Delete')}
        cancelText={t('Common.Cancel')}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default OtherMasters;
